import React from 'react';
import config from 'Config';
import { Auth } from 'lib/Amplify';
import axios from 'axios';

export const getProductOptions = (taxCode, leftStyleNumber, rightStyleNumber, solutionSku) => (
  Auth.currentAuthenticatedUser()
    .then((user) => axios.get(config.api.priceOptionsEndpoint, {
      params: {
        taxCode,
        leftStyleNumber,
        rightStyleNumber,
        solutionSku,
        countryCode: config.countryCode,
      },
      headers: {
        Authorization: `Bearer ${user
          .getSignInUserSession()
          .getIdToken()
          .getJwtToken()}`,
      },
    }))
    .then((result) => result.data.message.productOptions)
);

export const findAccount = (dateOfBirth) => Auth.currentAuthenticatedUser()
  .then((user) => axios.get(config.api.findAccountEndpoint, {
    params: { DOB: dateOfBirth },
    headers: {
      Authorization: `Bearer ${user
        .getSignInUserSession()
        .getIdToken()
        .getJwtToken()}`,
    },
  }))
  .catch((err) => {
    if (err.response && err.response.status === 404) {
      return null;
    }
    throw err;
  });

export const getAccount = () => Auth.currentAuthenticatedUser()
  .then((user) => axios.get(config.api.getAccountEndpoint, {
    headers: {
      Authorization: `Bearer ${user
        .getSignInUserSession()
        .getIdToken()
        .getJwtToken()}`,
    },
  }))
  .then((res) => res.data.message.account);

export const findSubscriptions = () => Auth.currentAuthenticatedUser()
  .then((user) => axios.get(config.api.subscriptionsEndpoint, {
    headers: {
      Authorization: `Bearer ${user
        .getSignInUserSession()
        .getIdToken()
        .getJwtToken()}`,
    },
  }))
  .then((res) => res.data.message.subscriptions);

export const getSubscription = (subscriptionId) => Auth.currentAuthenticatedUser().then((user) => axios.get(`${config.api.subscriptionsEndpoint}/${subscriptionId}`, {
  headers: {
    Authorization: `Bearer ${user
      .getSignInUserSession()
      .getIdToken()
      .getJwtToken()}`,
  },
}));

export const getStore = (storeId) => Auth.currentAuthenticatedUser().then((user) => axios.get(`${config.api.storesEndpoint}/${storeId}`, {
  headers: {
    Authorization: `Bearer ${user
      .getSignInUserSession()
      .getIdToken()
      .getJwtToken()}`,
  },
}));

export const changeUsage = (subscriptionId, changeUsageRequest) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(`${config.api.subscriptionsEndpoint}/${subscriptionId}/change_lens_amount`,
      { ...changeUsageRequest, countryCode: config.countryCode },
      {
        headers: {
          Authorization: `Bearer ${user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken()}`,
        },
      }));

export const validateBacsAccount = (sortCode, accountNumber) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(config.api.validateBacsAccountEndpoint,
      {
        sortCode,
        accountNumber,
        countryCode: 'GB',
      },
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
      })
  );

export const getBacsAddress = (sortCode, accountNumber) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(config.api.getBacsAddressEndpoint,
      {
        sortCode,
        accountNumber,
        countryCode: 'GB',
      },
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
      })
  );

export const updateBacsAccount = (sortCode, accountHolderName, accountNumber) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(config.api.updateBacsAccountEndpoint,
      {
        bacs: {
          account_holder_name: accountHolderName,
          sort_code: sortCode,
          account_number: accountNumber,
        }
      },
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
      })
  );

export const orderTopup = ({
  subscriptionId, addressId, paymentMethod, orderReason, rightQuantity, leftQuantity, solution, taxCode
}) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(`${config.api.subscriptionsEndpoint}/${subscriptionId}/topup`,
      {
        address_id: addressId,
        payment_method: paymentMethod,
        order_reason: orderReason,
        right_quantity: rightQuantity,
        left_quantity: leftQuantity,
        tax_code: taxCode,
        country_code: config.countryCode,
        solution_quantity: solution ? 1 : 0,
      },
      {
        headers: {
          Authorization: `Bearer ${user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken()}`,
        },
      }));

export const skipPack = (subscriptionId, packId) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(`${config.api.subscriptionsEndpoint}/${subscriptionId}/packs/${packId}/skip`,
      null,
      {
        headers: {
          Authorization: `Bearer ${user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken()}`,
        },
      }));

export const addAddress = (address, subscriptionId, packIds, switchToPrimary) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .post(config.api.addressesEndpoint,
      address,
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
        params: {
          subscriptionId,
          packIds: packIds ? packIds.join(',') : '',
          switchToPrimary: switchToPrimary || undefined,
        },
      }));

export const removeAddress = (addressId) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .delete(`${config.api.addressesEndpoint}/${addressId}`,
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
      }));

const addressLookup = (postcode, country) => Auth
  .currentAuthenticatedUser()
  .then((user) => axios
    .get(config.api.postcodeLookupEndpoint,
      {
        headers: {
          Authorization: `Bearer ${user.getSignInUserSession().getIdToken().getJwtToken()}`,
        },
        params: {
          postcode,
          country
        },
      }));

export const ApiContext = React.createContext({
  addressLookup
});
